import { z } from 'zod';
/**
 * Who reads the translation out loud (task 6).
 *
 * The translation itself always comes from the translation provider; this is
 * only the voice the listener hears it in. `openai` is that provider's own
 * built-in voice, which is how every room worked before this existed.
 * `elevenlabs` takes the translated text and speaks it again in a voice chosen
 * from that account — cleaner, but it can only start once a phrase is written,
 * so it is heard later.
 *
 * A list rather than a boolean because a third one is expected, and the
 * interface is built to take it as one more row.
 */
export const VOICE_PROVIDERS = ['openai', 'elevenlabs'];
export const voiceProviderSchema = z.enum(VOICE_PROVIDERS);
/**
 * ElevenLabs voice ids are short alphanumeric strings. The id goes into a URL
 * path on the way to the provider, so it is held to that shape here rather
 * than trusted.
 */
export const VOICE_ID_RE = /^[A-Za-z0-9]{1,64}$/;
export const voiceChoiceSchema = z.discriminatedUnion('provider', [
    z.object({ provider: z.literal('openai') }),
    z.object({
        provider: z.literal('elevenlabs'),
        voiceId: z.string().regex(VOICE_ID_RE),
        /**
         * The voice's name as the picker showed it, kept only so the creator's call
         * screen can say which voice is speaking. Display text, never an id.
         */
        name: z.string().max(120).optional(),
    }),
]);
/** The default, and what a room falls back to when a choice cannot be honoured. */
export const BUILT_IN_VOICE = { provider: 'openai' };
/** One voice the creator may pick for a language. Only what the picker shows. */
export const voiceOptionSchema = z.object({
    id: z.string().regex(VOICE_ID_RE),
    name: z.string().max(120),
    /** A short sample the provider hosts. Absent for some voices. */
    previewUrl: z.string().url().nullable(),
    /**
     * Whether the provider has checked this voice in this language (or labelled
     * it with it). Every voice can speak every room language; one that has not
     * been checked is more likely to carry an accent, and the picker says so.
     */
    verified: z.boolean(),
});
/**
 * The second voice's audio, as the provider is asked for it: 16-bit mono PCM
 * at this rate. Both ends name it from here, because the server asks for the
 * format and the browser has to decode exactly that.
 */
export const VOICE_SAMPLE_RATE = 24_000;
/**
 * What a seat set to ElevenLabs needs to speak the translation (task 6).
 *
 * `url` is the provider's WebSocket with a single-use token already in it: the
 * browser opens it directly, the way it opens WebRTC to the translation
 * provider, so speech does not pass through this server. The token is good
 * for one connection and a quarter of an hour, and the account key never
 * leaves the server.
 */
export const voiceSessionResponseSchema = z.object({
    url: z.string().url(),
    voiceId: z.string().regex(VOICE_ID_RE),
    model: z.string(),
    sampleRate: z.number().int().positive(),
    expiresAt: z.number().int().positive(),
});
export const voiceListResponseSchema = z.object({
    voices: z.array(voiceOptionSchema),
});
//# sourceMappingURL=voice.js.map